import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/config/axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, KPICard, Spinner, Alert, Modal, FormField, Button, FormReactSelect } from '../ui';
import { FormDatePicker } from '../ui';
import DirhamSymbol from '../components/DirhamSymbol';
import BulkImportModal from '../components/BulkImportModal';
import InvestorTimeline from '../components/InvestorTimeline';
import { useToastContext } from '../ui/feedback/ToastProvider';
import {
  Wallet,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Plus,
  Users,
  Percent,
  Link2,
  Receipt,
  FileText,
  Activity,
  Calculator,
  X,
  Calendar,
  ArrowLeftRight,
  Flag,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Clock,
  Maximize2,
  CheckCircle,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  ComposedChart,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush,
  ReferenceLine,
} from 'recharts';

// Unified theme colors - Emerald and Deep Teal
const COLORS = ['#0A7E66', '#014F43', '#1AB98E', '#4DC8A7', '#086552'];

// Custom YAxis tick component with DirhamSymbol
const CustomYAxisTick = ({ x, y, payload }) => {
  const value = payload.value;
  const formattedValue = value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const symbolSize = 12;
  const estimatedTextWidth = formattedValue.length * 7;
  const totalWidth = symbolSize + 4 + estimatedTextWidth;

  return (
    <g transform={`translate(${x},${y})`}>
      <foreignObject
        x={-totalWidth}
        y={-8}
        width={totalWidth}
        height={16}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            height: '100%',
            fontSize: '12px',
            color: 'currentColor'
          }}
          className="text-primary-600 dark:text-primary-400"
        >
          <DirhamSymbol size={symbolSize} inline />
          <span>{formattedValue}</span>
        </div>
      </foreignObject>
    </g>
  );
};

// Custom Tooltip component with DirhamSymbol
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '8px 12px',
        }}
        className="shadow-lg dark:bg-primary-800 dark:border-primary-700"
      >
        <p className="text-primary-900 dark:text-primary-50 font-medium mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p
            key={index}
            className="text-primary-900 dark:text-primary-50 flex items-center gap-1"
            style={{ color: entry.color }}
          >
            <span className="font-medium">{entry.name}:</span>
            {entry.name.includes('%') || entry.name.includes('ROI') ? (
              <span>{entry.value.toLocaleString()}%</span>
            ) : (
              <>
                <DirhamSymbol size={14} inline />
                <span>{entry.value.toLocaleString()}</span>
              </>
            )}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { error: showError, success: showSuccess } = useToastContext();
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState({
    monthlyProfitExpense: [],
    trendData: [],
    recentPayouts: [],
    payoutPagination: null
  });
  const [payoutPage, setPayoutPage] = useState(1);
  const [showROICalculator, setShowROICalculator] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [showInvestmentsModal, setShowInvestmentsModal] = useState(false);
  const [portfolioData, setPortfolioData] = useState(null);
  const [transactionActiveTab, setTransactionActiveTab] = useState('INR');
  const [showFullGraphModal, setShowFullGraphModal] = useState(false);
  const [inrFormData, setInrFormData] = useState({
    transactionAmountInr: '',
    date: new Date(),
    aedToUsdtRate: '',
    inrToAedRate: '',
    usdtSellingInr: ''
  });
  const [uaeFormData, setUaeFormData] = useState({
    amount: '',
    date: new Date(),
    usdtBuyingRate: '',
    usdtSellingRate: ''
  });

  // Profit Allocation State
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [allocationData, setAllocationData] = useState(null);
  const [allocationFormData, setAllocationFormData] = useState({
    recipientType: '',
    recipientId: '',
    amount: '',
    usePercentage: ''
  });
  const [users, setUsers] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [totalInvestedAmount, setTotalInvestedAmount] = useState(0);
  const [roiCalculatorData, setRoiCalculatorData] = useState({
    investment: '',
    days: '',
    basePercentage: '',
    // Admin-specific fields
    aedToUsdtRate: '',
    inrToAedRate: '',
    usdtSellingInr: '',
    totalUsdtSold: '',
  });
  const [calculatedROI, setCalculatedROI] = useState(null);
  const [defaultROI, setDefaultROI] = useState(null);
  const [investorRoiData, setInvestorRoiData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // Current month (1-12)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); // Current year

  // ROI data for timeseries chart - fetched from API
  const [roiData, setRoiData] = useState([]);
  const [isLiveData, setIsLiveData] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshingROI, setIsRefreshingROI] = useState(false);
  const [isRefreshingDashboard, setIsRefreshingDashboard] = useState(false);

  // Admin profit history state
  const [adminProfitHistory, setAdminProfitHistory] = useState([]);
  const [selectedAdminName, setSelectedAdminName] = useState('Admin A');
  const [adminProfitPeriod, setAdminProfitPeriod] = useState('7d');

  // ROI period filter state
  const [roiPeriod, setRoiPeriod] = useState('30d');

  // Daily Earnings Modal State
  const [showDailyEarningsModal, setShowDailyEarningsModal] = useState(false);
  const [dailyEarningsData, setDailyEarningsData] = useState([]);
  const [loadingEarnings, setLoadingEarnings] = useState(false);

  const fetchROIHistory = async (showLoading = false, period = roiPeriod) => {
    try {
      if (showLoading) {
        setIsRefreshingROI(true);
      }
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      const response = await api.get(`/api/dashboard/roi-history?t=${timestamp}&period=${period}`);
      if (response.data.success) {
        setRoiData(response.data.roiHistory || []);
        setIsLiveData(response.data.isLive || false);
        setLastUpdated(new Date()); // Update timestamp when data is fetched
        console.log('ROI data refreshed:', response.data.roiHistory.length, 'data points');
      }
    } catch (error) {
      console.error('Error fetching ROI history:', error);
      // Keep existing data on error
    } finally {
      if (showLoading) {
        setIsRefreshingROI(false);
      }
    }
  };

  const handleRefreshROI = async () => {
    try {
      setIsRefreshingROI(true);

      // First, trigger minute ROI calculation to generate new data
      await api.post('/api/scheduler/calculate-minute-roi');

      // Then fetch the updated ROI history
      await fetchROIHistory(false);

    } catch (error) {
      console.error('Error refreshing ROI:', error);
    } finally {
      setIsRefreshingROI(false);
    }
  };

  const fetchDailyEarnings = async () => {
    try {
      setLoadingEarnings(true);
      const response = await api.get('/api/dashboard/daily-earnings');
      if (response.data.success) {
        setDailyEarningsData(response.data.earnings || []);
      }
    } catch (error) {
      console.error('Error fetching daily earnings:', error);
      showError('Failed to fetch daily earnings');
    } finally {
      setLoadingEarnings(false);
    }
  };

  // Fetch admin profit history for admin dashboard
  const fetchAdminProfitHistory = async () => {
    if (user?.role !== 'admin') return;

    try {
      const params = {
        period: adminProfitPeriod,
        adminName: selectedAdminName
      };

      // Add month/year if period is not used
      if (!adminProfitPeriod) {
        params.month = selectedMonth;
        params.year = selectedYear;
      }

      const response = await api.get('/api/dashboard/admin-profit-history', { params });
      if (response.data.success) {
        setAdminProfitHistory(response.data.adminProfitHistory || []);
        // Update last updated timestamp
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Error fetching admin profit history:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/api/users');
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchBrokers = async () => {
    try {
      const response = await api.get('/api/brokers');
      setBrokers(response.data.brokers || []);
    } catch (error) {
      console.error('Error fetching brokers:', error);
    }
  };

  const fetchTotalInvestedAmount = async () => {
    try {
      const response = await api.get('/api/extra-profit/total-investment');
      setTotalInvestedAmount(response.data.totalInvestment || 0);
    } catch (error) {
      console.error('Error fetching total invested amount:', error);
      setTotalInvestedAmount(0);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchROIHistory(); // Fetch ROI history on mount

    // Fetch allocation data if admin
    if (user?.role === 'admin') {
      fetchUsers();
      fetchBrokers();
      fetchTotalInvestedAmount();
    }

    // Fetch investor ROI data if user is an investor
    if (user?.role === 'investor') {
      fetchInvestorRoiData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedMonth, selectedYear]);

  // Poll for ROI updates every minute
  useEffect(() => {
    if (!user) return;

    // Fetch immediately
    fetchROIHistory();

    // Set up interval to fetch every minute
    const interval = setInterval(() => {
      fetchROIHistory();
    }, 60000); // 60 seconds = 1 minute

    return () => clearInterval(interval);
  }, [user]);

  // Fetch admin profit history when filters change
  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAdminProfitHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, selectedAdminName, adminProfitPeriod]);

  // Fetch payouts when page changes (for investors)
  useEffect(() => {
    if (user?.role === 'investor' && payoutPage > 0) {
      // Only fetch chart data (which includes payouts) when page changes
      const fetchPayouts = async () => {
        try {
          const params = {
            payoutPage: payoutPage,
            payoutLimit: 10,
            month: selectedMonth,
            year: selectedYear
          };
          const chartsRes = await api.get('/api/dashboard/charts', { params });
          const charts = chartsRes.data.charts || {};
          setChartData(prev => ({
            ...prev,
            recentPayouts: charts.recentPayouts || [],
            payoutPagination: charts.payoutPagination || null
          }));
        } catch (error) {
          console.error('Error fetching payouts:', error);
        }
      };
      fetchPayouts();
    }
  }, [payoutPage, user?.role, selectedMonth, selectedYear]);

  // Calculate default ROI for non-admin users
  useEffect(() => {
    if (user && user.role !== 'admin') {
      let defaultROIValue = null;

      if (user.role === 'investor') {
        // For investors, use average of roi_min and roi_max from investorRoiData
        if (investorRoiData) {
          const roiMin = investorRoiData.roi_min || 0;
          const roiMax = investorRoiData.roi_max || 0;

          if (roiMin > 0 && roiMax > 0) {
            defaultROIValue = (roiMin + roiMax) / 2;
          } else if (roiMax > 0) {
            defaultROIValue = roiMax;
          } else if (roiMin > 0) {
            defaultROIValue = roiMin;
          } else {
            defaultROIValue = 10; // Fallback
          }
        } else {
          // If investorRoiData is not loaded yet, use fallback
          defaultROIValue = 10;
        }
      } else if (user.role === 'broker') {
        // Brokers view commission data, not calculate ROI for themselves
        // Use a default value for any ROI calculator functionality
        defaultROIValue = 10;
      }

      setDefaultROI(defaultROIValue);
    }
  }, [user, investorRoiData]);

  // Auto-populate ROI percentage when modal opens for non-admin users
  useEffect(() => {
    if (showROICalculator && user?.role !== 'admin') {
      // Always set basePercentage for non-admin users, even if defaultROI is not yet calculated
      // It will be updated when defaultROI is available
      if (defaultROI) {
        setRoiCalculatorData(prev => ({
          ...prev,
          basePercentage: defaultROI.toFixed(2)
        }));
      } else {
        // Set a temporary default while waiting for ROI data
        setRoiCalculatorData(prev => ({
          ...prev,
          basePercentage: '10.00'
        }));
      }
    } else if (showROICalculator && user?.role === 'admin') {
      // Reset for admin when modal opens
      setRoiCalculatorData(prev => ({
        ...prev,
        basePercentage: ''
      }));
    }
  }, [showROICalculator, defaultROI, user]);



  const fetchInvestorRoiData = async () => {
    try {
      // Fetch broker-investor relationship to get ROI values
      const response = await api.get('/api/broker-investors', {
        params: { investor_id: user?._id }
      });
      const relationships = response.data.brokerInvestors || [];
      const relationship = relationships[0]; // Get first relationship if exists

      if (relationship) {
        // Priority 1: BrokerInvestor relationship ROI values
        if (relationship.roi_min !== undefined && relationship.roi_max !== undefined) {
          setInvestorRoiData({
            roi_min: relationship.roi_min,
            roi_max: relationship.roi_max,
            source: 'broker_investor'
          });
          return;
        }
      }

      // Priority 2: User's own ROI values
      if (user?.roi_min !== undefined && user?.roi_max !== undefined &&
        (user.roi_min > 0 || user.roi_max > 0)) {
        setInvestorRoiData({
          roi_min: user.roi_min,
          roi_max: user.roi_max,
          source: 'user'
        });
        return;
      }

      // Priority 3: Broker's default ROI values (if relationship exists)
      if (relationship?.broker_id) {
        try {
          const brokerResponse = await api.get(`/api/brokers/${relationship.broker_id}`);
          const broker = brokerResponse.data.broker;
          if (broker && broker.min_roi !== undefined && broker.max_roi !== undefined &&
            (broker.min_roi > 0 || broker.max_roi > 0)) {
            setInvestorRoiData({
              roi_min: broker.min_roi,
              roi_max: broker.max_roi,
              source: 'broker'
            });
            return;
          }
        } catch (error) {
          console.error('Error fetching broker default ROI:', error);
        }
      }

      // No ROI data found
      setInvestorRoiData(null);
    } catch (error) {
      console.error('Error fetching investor ROI data:', error);
      setInvestorRoiData(null);
    }
  };

  const fetchPortfolioData = async () => {
    try {
      const [portfolioRes, transactionsRes] = await Promise.all([
        api.get('/api/portfolio'),
        api.get('/api/portfolio/transactions?limit=100')
      ]);

      if (portfolioRes.data.success) {
        setPortfolioData({
          ...portfolioRes.data.portfolio,
          transactions: transactionsRes.data.transactions || []
        });
      }
    } catch (error) {
      console.error('Error fetching portfolio data:', error);
      showError('Failed to load investment details');
    }
  };

  const fetchDashboardData = async () => {
    try {
      const paramsWithTs = { t: Date.now() };
      const kpisRes = await api.get('/api/dashboard/kpis', { params: paramsWithTs });
      setKpis(kpisRes.data.kpis);

      // Fetch chart data for all roles
      try {
        const params = {
          month: selectedMonth,
          year: selectedYear
        };
        if (user?.role === 'investor') {
          params.payoutPage = payoutPage;
          params.payoutLimit = 10;
        }
        const chartsRes = await api.get('/api/dashboard/charts', {
          params: { ...params, t: Date.now() }
        });
        const charts = chartsRes.data.charts || {};

        // Update chart data with real data
        setChartData(prev => ({
          monthlyProfitExpense: charts.monthlyProfitExpense || prev.monthlyProfitExpense || [],
          trendData: charts.trendData || prev.trendData || [],
          recentPayouts: charts.recentPayouts || [],
          payoutPagination: charts.payoutPagination || null
        }));
        setLastUpdated(new Date());
      } catch (chartError) {
        console.error('Error fetching chart data:', chartError);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Set defaults on error
      setKpis({
        portfolioValue: 0,
        totalInvested: 0,
        totalGainLoss: 0,
        totalGainLossPercent: 0,
        totalTransactions: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshDashboard = async () => {
    if (isRefreshingDashboard) return;
    setIsRefreshingDashboard(true);
    try {
      // Refresh should be read-only: re-fetch latest persisted values without mutating ROI.
      await fetchDashboardData();
      await fetchROIHistory(false);
      showSuccess('Dashboard refreshed with latest values');
    } catch (error) {
      console.error('Error refreshing dashboard:', error);
      showError(error.response?.data?.message || 'Failed to refresh dashboard');
    } finally {
      setIsRefreshingDashboard(false);
    }
  };

  // Use real chart data if available, otherwise use empty data
  // For investors, only show profit (no expense)
  // Transform chart data based on user role
  const barChartData = chartData.monthlyProfitExpense.length > 0
    ? chartData.monthlyProfitExpense.map(item => {
      if (user?.role === 'broker') {
        // For brokers, use commission data
        return {
          month: item.month,
          commission: item.commission || 0,
          profit: 0, // Not used for brokers
          expense: 0 // Not used for brokers
        };
      } else {
        // For admin and investors, use profit/expense data
        return {
          month: item.month,
          profit: item.profit || 0,
          expense: item.expense || 0
        };
      }
    })
    : [
      { month: 'Jan', profit: 0, expense: 0, commission: 0 },
      { month: 'Feb', profit: 0, expense: 0, commission: 0 },
      { month: 'Mar', profit: 0, expense: 0, commission: 0 },
      { month: 'Apr', profit: 0, expense: 0, commission: 0 },
      { month: 'May', profit: 0, expense: 0, commission: 0 },
      { month: 'Jun', profit: 0, expense: 0, commission: 0 },
    ];

  // Use real trend data from API, fallback to empty data if not available
  const trendChartData = chartData.trendData && chartData.trendData.length > 0
    ? chartData.trendData
    : [
      { week: 'Week 1', profit: 0, roi: 0 },
      { week: 'Week 2', profit: 0, roi: 0 },
      { week: 'Week 3', profit: 0, roi: 0 },
      { week: 'Week 4', profit: 0, roi: 0 },
      { week: 'Week 5', profit: 0, roi: 0 },
      { week: 'Week 6', profit: 0, roi: 0 },
    ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  // Broker-specific KPIs
  const brokerKpiCards = user?.role === 'broker' ? [
    {
      title: 'Total Commission',
      value: (
        <span className="flex items-center gap-1">
          <DirhamSymbol size={20} inline />
          {(kpis?.totalCommission || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
      icon: DollarSign,
      color: 'primary',
      change: 'Total earned (AED)',
      isPositive: undefined,
      href: '/broker-investors',
    },
    {
      title: 'Monthly Commission',
      value: (
        <span className="flex items-center gap-1">
          <DirhamSymbol size={20} inline />
          {(kpis?.monthlyCommission || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
      icon: TrendingUp,
      color: 'success',
      change: 'This month (AED)',
      isPositive: true,
      href: '/broker-investors',
    },
    {
      title: 'Total Investors',
      value: kpis?.totalInvestors || 0,
      icon: Users,
      color: 'accent',
      change: 'Active investors',
      isPositive: undefined,
      href: '/broker-investors',
    },
    {
      title: 'Average ROI',
      value: `${(kpis?.averageROI || 0).toFixed(2)}%`,
      icon: Percent,
      color: 'secondary',
      change: 'Portfolio average',
      isPositive: true,
      href: '/broker-investors',
    },
  ] : [];

  // Investor-specific KPIs
  const investorKpiCards = user?.role === 'investor' ? [
    {
      title: 'Total Invested Amount',
      value: (
        <div>
          <div className="text-2xl sm:text-3xl font-bold">
            ${(kpis?.totalInvestedAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-xs text-primary-600 dark:text-primary-400 mt-1 flex items-center gap-1">
            <DirhamSymbol size={12} inline />
            {((kpis?.totalInvestedAmount || 0) * (user?.aed_conversion_rate || 3.662)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED
          </div>
        </div>
      ),
      icon: DollarSign,
      color: 'primary',
      change: 'Investment Amount',
      isPositive: undefined,
      onClick: () => {
        setShowInvestmentsModal(true);
        fetchPortfolioData();
      },
    },
    {
      title: "Today's Profit",
      value: (
        <span className="flex items-center gap-1">
          <DirhamSymbol size={20} inline />
          {(kpis?.todayProfitAED || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
      icon: TrendingUp,
      color: 'success',
      change: 'Daily Profit',
      isPositive: (kpis?.todayProfitAED || 0) >= 0,
      onClick: () => {
        setShowDailyEarningsModal(true);
        fetchDailyEarnings();
      },
    },
    {
      title: 'Total Payout',
      value: (
        <span className="flex items-center gap-1">
          <DirhamSymbol size={20} inline />
          {(kpis?.totalPayout || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
      icon: Wallet,
      color: 'info',
      change: 'Total Payouts',
      isPositive: undefined,
    },
    {
      title: 'Profit Balance',
      value: (
        <span className="flex items-center gap-1">
          <DirhamSymbol size={20} inline />
          {(kpis?.profitBalance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
      icon: TrendingUp,
      color: 'secondary',
      change: 'Profit - Payout',
      isPositive: kpis?.profitBalance >= 0,
    },
  ] : [];

  // Standard KPIs for other roles (non-broker, non-investor)
  const standardKpiCards = user?.role !== 'broker' && user?.role !== 'investor' ? [
    {
      title: 'Total Transactions',
      value: kpis?.totalTransactions || 0,
      icon: Activity,
      color: 'secondary', // Deep teal for distinction
      change: 'All time',
      isPositive: undefined,
      href: '/portfolio', // Navigate to portfolio page to see transactions
    },
  ] : [];

  // Admin-specific additional KPIs - Arranged in logical order
  const adminKpiCards = user?.role === 'admin' ? [
    // 1. Starting point: Initial investments
    {
      title: 'Initial Investment (USD)',
      value: (
        <div>
          <div className="text-2xl sm:text-3xl font-bold">
            ${(kpis?.initialInvestmentUSD || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-xs text-primary-600 dark:text-primary-400 mt-1 flex items-center gap-1">
            <DirhamSymbol size={12} inline />
            {(kpis?.totalInvestedAED || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED
          </div>
        </div>
      ),
      icon: DollarSign,
      color: 'primary',
      change: 'Total USD Investment',
      isPositive: undefined,
    },
    // 2. Performance metrics: Profit and balance
    {
      title: 'Total Cash in Hand',
      value: (
        <span className="flex items-center gap-1">
          <DirhamSymbol size={20} inline />
          {(kpis?.totalCashInHand || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
      icon: Wallet,
      color: 'success',
      change: 'Inv AED + Profit - Exp - Payout',
      isPositive: true, // Cash in hand is generally positive
    },
    {
      title: 'Total Profit AED',
      value: (
        <span className="flex items-center gap-1">
          <DirhamSymbol size={20} inline />
          {(kpis?.totalProfitAED || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
      icon: TrendingUp,
      color: 'primary',
      change: 'Total Generated Trade Profit',
      isPositive: (kpis?.totalProfitAED || 0) >= 0,
    },
    {
      title: 'Profit Balance',
      value: (
        <span className="flex items-center gap-1">
          <DirhamSymbol size={20} inline />
          {(kpis?.profitBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
      icon: Calculator, // Changed icon to distinguish
      color: 'secondary',
      change: 'Profit AED - Payout - Expense',
      isPositive: (kpis?.profitBalance || 0) >= 0, // dynamic color based on value
    },
    // 3. Distribution: Investors profit, broker commission and payouts
    {
      title: 'Investors Profit',
      value: (
        <span className="flex items-center gap-1">
          <DirhamSymbol size={20} inline />
          {(kpis?.investorsProfit || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
      icon: Users,
      color: 'info',
      change: 'Total Investors Profit (earned by investors)',
      isPositive: kpis?.investorsProfit >= 0,
    },
    {
      title: 'Total Broker Commission',
      value: (
        <span className="flex items-center gap-1">
          <DirhamSymbol size={20} inline />
          {(kpis?.totalBrokerCommission || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
      icon: DollarSign,
      color: 'accent',
      change: 'Sum of all broker commissions',
      isPositive: kpis?.totalBrokerCommission >= 0,
    },
    {
      title: 'Total Payout',
      value: (
        <span className="flex items-center gap-1">
          <DirhamSymbol size={20} inline />
          {(kpis?.totalPayout || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
      icon: FileText,
      color: 'accent',
      change: 'Total Payout Amount',
      isPositive: undefined,
    },
    // 4. Expenses
    {
      title: 'Admin Expense',
      value: (
        <span className="flex items-center gap-1">
          <DirhamSymbol size={20} inline />
          {(kpis?.adminExpense || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
      icon: Receipt,
      color: 'error',
      change: 'Admin Expense Amount',
      isPositive: undefined,
    },
  ] : [];

  // Arrange cards in meaningful order for admin: Initial investments → Profit/Balance → Distribution → Expenses → Activity
  const kpiCards = user?.role === 'broker'
    ? brokerKpiCards
    : user?.role === 'admin'
      ? [
        ...adminKpiCards,
        {
          title: 'Total Transactions',
          value: kpis?.totalTransactions || 0,
          icon: Activity,
          color: 'secondary',
          change: 'All time',
          isPositive: undefined,
          href: '/portfolio',
        }
      ] // Admin KPIs first, then Total Transactions
      : user?.role === 'investor'
        ? investorKpiCards // Investor-specific KPIs
        : standardKpiCards;

  const handleROICalculatorChange = (e) => {
    const { name, value } = e.target;
    setRoiCalculatorData(prev => ({ ...prev, [name]: value }));
    setCalculatedROI(null); // Reset calculation when inputs change
  };

  const calculateROI = () => {
    if (user?.role === 'admin') {
      // Admin calculation with new formula
      const days = parseFloat(roiCalculatorData.days) || 0;
      const aedToUsdtRate = parseFloat(roiCalculatorData.aedToUsdtRate) || 0;
      const inrToAedRate = parseFloat(roiCalculatorData.inrToAedRate) || 0;
      const usdtSellingInr = parseFloat(roiCalculatorData.usdtSellingInr) || 0;
      const totalUsdtSold = parseFloat(roiCalculatorData.totalUsdtSold) || 0;

      // Validate required fields
      if (!aedToUsdtRate || !inrToAedRate || !usdtSellingInr || !totalUsdtSold ||
        aedToUsdtRate <= 0 || inrToAedRate <= 0 || usdtSellingInr <= 0 || totalUsdtSold <= 0) {
        return;
      }

      // Calculate DAILY values
      // DAILY TOTAL INR SOLD = TOTAL USDT SOLD * USDT SELLING INR
      const dailyTotalInrSold = totalUsdtSold * usdtSellingInr;

      // DAILY CASH IN HAND AED = IF(INR TO AED RATE=0 THEN 0 ELSE DAILY TOTAL INR SOLD/INR TO AED RATE)
      const dailyCashInHandAed = inrToAedRate === 0 ? 0 : dailyTotalInrSold / inrToAedRate;

      // DAILY AED TO USDT TOTAL (Cost) = TOTAL USDT SOLD * AED TO USDT RATE
      const dailyAedToUsdtCost = totalUsdtSold * aedToUsdtRate;

      // DAILY PROFIT AED
      const isNumberDailyCashInHand = !isNaN(dailyCashInHandAed) && isFinite(dailyCashInHandAed);
      const isNumberDailyCost = !isNaN(dailyAedToUsdtCost) && isFinite(dailyAedToUsdtCost);
      const dailyProfitAed = (isNumberDailyCashInHand && isNumberDailyCost)
        ? dailyCashInHandAed - dailyAedToUsdtCost
        : 0;

      // Calculate TOTAL values based on days
      const effectiveDays = days > 0 ? days : 0;

      const totalProfitAed = dailyProfitAed * effectiveDays;
      const totalCashInHandAed = dailyCashInHandAed * effectiveDays;
      const totalInrSold = dailyTotalInrSold * effectiveDays;

      // ROI = (TOTAL PROFIT / PRINCIPAL COST) * 100
      let roiPercentage = 0;
      if (dailyAedToUsdtCost > 0) {
        roiPercentage = (totalProfitAed / dailyAedToUsdtCost) * 100;
      }

      setCalculatedROI({
        percentage: roiPercentage,
        returnAmount: totalProfitAed,
        returnAmountAED: totalProfitAed,
        totalAmount: totalCashInHandAed,
        investment: totalUsdtSold, // Show Principal Amount
        days: days || 0,
        basePercentage: roiPercentage,
        // Additional admin-specific values
        totalInrSold,
        cashInHandAed: totalCashInHandAed,
        aedToUsdtTotal: dailyAedToUsdtCost * effectiveDays,
        profitAed: totalProfitAed,
      });
    } else {
      // Non-admin calculation (existing logic)
      const investment = parseFloat(roiCalculatorData.investment);
      const days = parseFloat(roiCalculatorData.days);
      const basePercentage = parseFloat(roiCalculatorData.basePercentage);

      if (!investment || !days || !basePercentage || investment <= 0 || days <= 0 || basePercentage <= 0) {
        return;
      }

      // Calculate ROI using the provided ROI percentage
      // Formula: Total return = (invested amount * ROI%) / 100
      const roiPercentage = basePercentage;

      // Calculate return amount in USD
      let returnAmount = (investment * roiPercentage) / 100;

      // If days > 30, prorate the return amount
      if (days > 30) {
        returnAmount = (returnAmount / 30) * days;
      }

      const totalAmount = investment + returnAmount;

      // Convert return amount to AED using conversion rate
      // Default conversion rate is 3.36 if user doesn't have one set
      const aedConversionRate = user?.aed_conversion_rate || 3.36;
      const returnAmountAED = returnAmount * aedConversionRate;

      setCalculatedROI({
        percentage: roiPercentage,
        returnAmount,
        returnAmountAED,
        totalAmount,
        investment,
        days,
        basePercentage
      });
    }
  };

  const handleCloseROICalculator = () => {
    setShowROICalculator(false);
    // Reset to empty, but preserve default ROI for non-admin users
    if (user?.role === 'admin') {
      setRoiCalculatorData({
        investment: '',
        days: '',
        basePercentage: '',
        aedToUsdtRate: '',
        inrToAedRate: '',
        usdtSellingInr: '',
        totalUsdtSold: '',
      });
    } else {
      setRoiCalculatorData({
        investment: '',
        days: '',
        basePercentage: defaultROI ? defaultROI.toFixed(2) : '',
        aedToUsdtRate: '',
        inrToAedRate: '',
        usdtSellingInr: '',
        totalUsdtSold: '',
      });
    }
    setCalculatedROI(null);
  };

  const handleInrSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!inrFormData.transactionAmountInr || !inrFormData.aedToUsdtRate || !inrFormData.inrToAedRate || !inrFormData.usdtSellingInr) {
        showError('All fields are required');
        return;
      }

      const dateValue = inrFormData.date instanceof Date
        ? inrFormData.date.toISOString()
        : new Date(inrFormData.date).toISOString();

      const transactionAmountInr = parseFloat(inrFormData.transactionAmountInr);
      const aedToUsdtRate = parseFloat(inrFormData.aedToUsdtRate);

      // Convert transaction amount (USDT) to AED for comparison
      const transactionAmountAED = transactionAmountInr * aedToUsdtRate;

      // Check if transaction amount exceeds total invested amount
      const shouldShowAllocation = transactionAmountAED > totalInvestedAmount;

      const response = await api.post('/api/portfolio/create-transaction', {
        transactionType: 'INR',
        date: dateValue,
        transactionAmountInr: transactionAmountInr,
        aedToUsdtRate: aedToUsdtRate,
        inrToAedRate: parseFloat(inrFormData.inrToAedRate),
        usdtSellingInr: parseFloat(inrFormData.usdtSellingInr)
      });

      // Only show allocation modal if transaction amount > total invested amount AND there's profit
      if (shouldShowAllocation && response.data.profitAed > 0) {
        setAllocationData({
          transactionId: response.data.transaction._id,
          transactionType: 'INR',
          profitAed: response.data.profitAed,
          transaction: response.data.transaction
        });
        setShowTransactionModal(false);
        setShowAllocationModal(true);
      } else {
        showSuccess('Transaction created successfully');
        setShowTransactionModal(false);
        fetchDashboardData(); // Refresh dashboard data
      }

      // Reset form
      setInrFormData({
        transactionAmountInr: '',
        date: new Date(),
        aedToUsdtRate: '',
        inrToAedRate: '',
        usdtSellingInr: ''
      });
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to create transaction');
    }
  };

  const handleUaeSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!uaeFormData.amount || !uaeFormData.usdtBuyingRate || !uaeFormData.usdtSellingRate) {
        showError('All fields are required');
        return;
      }

      const dateValue = uaeFormData.date instanceof Date
        ? uaeFormData.date.toISOString()
        : new Date(uaeFormData.date).toISOString();

      const transactionAmountAED = parseFloat(uaeFormData.amount);

      // Check if transaction amount exceeds total invested amount
      const shouldShowAllocation = transactionAmountAED > totalInvestedAmount;

      const response = await api.post('/api/portfolio/create-transaction', {
        transactionType: 'UAE',
        date: dateValue,
        amount: transactionAmountAED,
        usdtBuyingRate: parseFloat(uaeFormData.usdtBuyingRate),
        usdtSellingRate: parseFloat(uaeFormData.usdtSellingRate)
      });

      // Only show allocation modal if transaction amount > total invested amount AND there's profit
      if (shouldShowAllocation && response.data.profitAed > 0) {
        setAllocationData({
          transactionId: response.data.transaction._id,
          transactionType: 'UAE',
          profitAed: response.data.profitAed,
          transaction: response.data.transaction
        });
        setShowTransactionModal(false);
        setShowAllocationModal(true);
      } else {
        showSuccess('Transaction created successfully');
        setShowTransactionModal(false);
        await fetchDashboardData(); // Refresh dashboard data
      }

      // Reset form
      setUaeFormData({
        amount: '',
        date: new Date(),
        usdtBuyingRate: '',
        usdtSellingRate: ''
      });
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to create transaction');
    }
  };

  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined || isNaN(value)) return '-';
    return value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  /**
   * Handle extra profit allocation
   */
  const handleAllocateProfit = async (e) => {
    e.preventDefault();

    try {
      // Validate recipient selection
      if (!allocationFormData.recipientId) {
        showError('Please select an investor or broker');
        return;
      }

      // Calculate allocation amount based on user selection
      const amount = allocationFormData.usePercentage === '50%'
        ? allocationData.profitAed * 0.5
        : allocationFormData.usePercentage === 'max'
          ? allocationData.profitAed
          : parseFloat(allocationFormData.amount) || 0;

      // Validate amount
      if (amount <= 0) {
        showError('Please enter a valid amount or select a percentage');
        return;
      }

      if (amount > allocationData.profitAed) {
        showError(`Amount cannot exceed available profit (${formatNumber(allocationData.profitAed)} AED)`);
        return;
      }

      // Call backend API to allocate profit
      const response = await api.post('/api/portfolio/allocate-profit', {
        transactionId: allocationData.transactionId,
        recipientType: allocationFormData.recipientType,
        recipientId: allocationFormData.recipientId,
        amount: amount,
        usePercentage: allocationFormData.usePercentage
      });

      // Extract allocation details from response
      const { allocation } = response.data;

      // Calculate number of ProfitAllocation records created
      const profitAllocationCount = allocation?.allocations?.length || 0;
      const remainingAmount = allocation?.remaining_amount || 0;

      // Show detailed success message
      let successMessage = `Profit allocated successfully! `;
      successMessage += `${formatNumber(amount)} AED allocated to ${allocationFormData.recipientType}. `;

      if (remainingAmount > 0) {
        successMessage += `Remaining ${formatNumber(remainingAmount)} AED distributed to admins. `;
      }

      if (profitAllocationCount > 0) {
        successMessage += `${profitAllocationCount} allocation record(s) created.`;
      }

      showSuccess(successMessage);

      // Reset form and close modal
      setShowAllocationModal(false);
      setAllocationData(null);
      setAllocationFormData({
        recipientType: '',
        recipientId: '',
        amount: '',
        usePercentage: ''
      });

      // Refresh dashboard data
      fetchDashboardData();

    } catch (error) {
      console.error('Error allocating profit:', error);

      // Show detailed error message
      const errorMessage = error.response?.data?.message
        || error.message
        || 'Failed to allocate profit. Please try again.';

      showError(errorMessage);
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary-900 dark:text-primary-50">
            Dashboard
          </h1>
          <p className="text-primary-600 dark:text-primary-400 mt-1.5 text-sm sm:text-base">
            Welcome back, {user?.firstName}!
          </p>
        </div>
        {user?.role === 'admin' && (
          <div className="flex gap-3">
            <Button
              onClick={() => setShowBulkImportModal(true)}
              variant="outline"
              leftIcon={<Plus size={20} />}
            >
              Import
            </Button>
            <Button
              onClick={() => setShowTransactionModal(true)}
              leftIcon={<Plus size={20} />}
              variant="primary"
            >
              Add Transaction
            </Button>
          </div>
        )}
        {user?.role === 'investor' && (
          <div className="flex items-center gap-2">
            <Button
              onClick={handleRefreshDashboard}
              variant="outline"
              leftIcon={<RefreshCw size={16} className={isRefreshingDashboard ? 'animate-spin' : ''} />}
              disabled={isRefreshingDashboard}
            >
              Refresh
            </Button>
            {lastUpdated && (
              <span className="text-xs text-primary-600 dark:text-primary-400">
                Updated {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Daily Timeline - For Investors */}
      {user?.role === 'investor' && <InvestorTimeline />}

      {/* ROI Calculator Notification */}
      <div className="relative">
        <Alert variant="info" className="border-2">
          <div className="flex items-center justify-between w-full gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="flex-shrink-0">
                <Calculator className="text-info-600 dark:text-info-400" size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-info-900 dark:text-info-50">
                  Know Your ROI
                </p>
                <p className="text-sm text-info-700 dark:text-info-300 mt-0.5">
                  Calculate your potential return on investment based on your investment amount and duration
                </p>
              </div>
            </div>
            <div className="flex-shrink-0">
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowROICalculator(true)}
                leftIcon={<Calculator size={16} />}
              >
                Calculate ROI
              </Button>
            </div>
          </div>
        </Alert>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
        {kpiCards.map((kpi, index) => {
          const Icon = kpi.icon;
          return (
            <KPICard
              key={index}
              title={kpi.title}
              value={kpi.value}
              change={kpi.change}
              isPositive={kpi.isPositive}
              icon={<Icon size={24} />}
              color={kpi.color}
              href={kpi.href}
              onClick={kpi.onClick}
            />
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-4 sm:gap-5 lg:gap-6">
        {/* Bar Chart - Monthly Profit vs Expense (or Monthly Profit only for investors, or Monthly Commission for brokers) */}
        <Card hover={false}>
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold text-primary-900 dark:text-primary-50">
              {user?.role === 'investor'
                ? 'Monthly Profit'
                : user?.role === 'broker'
                  ? 'Monthly Commission'
                  : 'Monthly Expense'}
            </h2>
            <div className="flex items-center gap-2">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="px-3 py-1.5 text-sm border border-primary-200 dark:border-primary-700 rounded-lg bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="1">January</option>
                <option value="2">February</option>
                <option value="3">March</option>
                <option value="4">April</option>
                <option value="5">May</option>
                <option value="6">June</option>
                <option value="7">July</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-3 py-1.5 text-sm border border-primary-200 dark:border-primary-700 rounded-lg bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={barChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="stroke-primary-200 dark:stroke-primary-700" />
              <XAxis
                dataKey="month"
                tick={{ fill: 'currentColor' }}
                className="text-primary-600 dark:text-primary-400"
              />
              <YAxis
                tick={<CustomYAxisTick />}
                className="text-primary-600 dark:text-primary-400"
              />
              <Tooltip content={<CustomTooltip />} />
              {user?.role === 'broker' ? (
                <>
                  <Legend />
                  <Bar dataKey="commission" fill="#0A7E66" name="Commission" radius={[8, 8, 0, 0]} />
                </>
              ) : (
                <>
                  {user?.role !== 'investor' && <Legend />}
                  {user?.role === 'investor' && (
                    <Bar dataKey="profit" fill="#0A7E66" name="Profit" radius={[8, 8, 0, 0]} />
                  )}
                  {user?.role !== 'investor' && (
                    <Bar dataKey="expense" fill="#DC2626" name="Expense" radius={[8, 8, 0, 0]} />
                  )}
                </>
              )}
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Admin Profit History Chart - Removed as requested */}

      {/* Recent Payouts History - For Investors */}
      {user?.role === 'investor' && (
        <Card hover={false}>
          <h2 className="text-lg font-semibold text-primary-900 dark:text-primary-50 mb-5">
            Recent Payouts
          </h2>
          <div className="space-y-3">
            {chartData.recentPayouts && chartData.recentPayouts.length > 0 ? (
              chartData.recentPayouts.map((payout) => (
                <div
                  key={payout._id}
                  onClick={() => {
                    setSelectedPayout(payout);
                    setShowPayoutModal(true);
                  }}
                  className="p-4 bg-primary-50 dark:bg-primary-900/30 rounded-lg border border-primary-200 dark:border-primary-700 hover:bg-primary-100 dark:hover:bg-primary-900/50 cursor-pointer transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-primary-200 dark:bg-primary-800 rounded-lg">
                        <Receipt size={20} className="text-primary-600 dark:text-primary-400" />
                      </div>
                      <div>
                        <p className="font-semibold text-primary-900 dark:text-primary-50">
                          Payout #{payout._id.toString().slice(-6)}
                        </p>
                        <p className="text-sm text-primary-600 dark:text-primary-400">
                          {new Date(payout.date).toISOString().split('T')[0]}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-primary-900 dark:text-primary-50 flex items-center gap-1 justify-end">
                        <DirhamSymbol size={18} inline />
                        {(payout.amt || payout.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-primary-500 dark:text-primary-500 mt-1">
                        Click for details
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-primary-500 dark:text-primary-400">
                <Receipt size={48} className="mx-auto mb-2 opacity-50" />
                <p>No payouts found</p>
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {chartData.payoutPagination && (
            <div className="flex flex-col sm:flex-row items-center justify-between mt-6 pt-4 border-t border-primary-200 dark:border-primary-700 gap-4">
              <div className="text-sm text-primary-600 dark:text-primary-400">
                {chartData.payoutPagination.totalPayouts > 0 ? (
                  <>
                    Showing {((chartData.payoutPagination.currentPage - 1) * chartData.payoutPagination.limit) + 1} to{' '}
                    {Math.min(chartData.payoutPagination.currentPage * chartData.payoutPagination.limit, chartData.payoutPagination.totalPayouts)} of{' '}
                    {chartData.payoutPagination.totalPayouts} payouts
                  </>
                ) : (
                  <>No payouts found</>
                )}
              </div>
              {chartData.payoutPagination.totalPayouts > 0 && (
                <div className="flex items-center gap-2 flex-wrap justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPayoutPage(prev => Math.max(1, prev - 1))}
                    disabled={chartData.payoutPagination.currentPage === 1 || chartData.payoutPagination.totalPages <= 1}
                    leftIcon={<ChevronLeft size={16} />}
                  >
                    Previous
                  </Button>
                  {chartData.payoutPagination.totalPages > 1 && (
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, chartData.payoutPagination.totalPages) }, (_, i) => {
                        let pageNum;
                        if (chartData.payoutPagination.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (chartData.payoutPagination.currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (chartData.payoutPagination.currentPage >= chartData.payoutPagination.totalPages - 2) {
                          pageNum = chartData.payoutPagination.totalPages - 4 + i;
                        } else {
                          pageNum = chartData.payoutPagination.currentPage - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={chartData.payoutPagination.currentPage === pageNum ? "primary" : "outline"}
                            size="sm"
                            onClick={() => setPayoutPage(pageNum)}
                            className="min-w-[40px]"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPayoutPage(prev => Math.min(chartData.payoutPagination.totalPages, prev + 1))}
                    disabled={chartData.payoutPagination.currentPage === chartData.payoutPagination.totalPages || chartData.payoutPagination.totalPages <= 1}
                    rightIcon={<ChevronRight size={16} />}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Full Width ROI Timeseries Chart - Hidden for Admin */}
      {user?.role !== 'admin' && (
        <Card hover={false}>
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold text-primary-900 dark:text-primary-50">
              {user?.role === 'broker'
                ? 'Commission Live Movement (Per Minute)'
                : 'ROI Live Movement (Per Minute)'}
            </h2>
            <div className="flex items-center gap-3">
              {(user?.role === 'investor' || user?.role === 'broker') && (
                <>
                  <select
                    value={roiPeriod}
                    onChange={(e) => {
                      setRoiPeriod(e.target.value);
                      fetchROIHistory(true, e.target.value);
                    }}
                    className="px-3 py-1.5 text-sm border border-primary-200 dark:border-primary-700 rounded-lg bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="5m">Last 5 Minutes</option>
                    <option value="15m">Last 15 Minutes</option>
                    <option value="30m">Last 30 Minutes</option>
                    <option value="1h">Last 1 Hour</option>
                    <option value="24h">Last 24 Hours</option>
                    <option value="7d">Last 7 Days</option>
                    <option value="30d">Last 30 Days</option>
                  </select>
                  {lastUpdated && (
                    <div className="flex items-center gap-1.5 text-xs text-primary-600 dark:text-primary-400">
                      <Clock size={14} />
                      <span>
                        Last updated: {lastUpdated.toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })}
                      </span>
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefreshROI}
                    disabled={isRefreshingROI}
                    leftIcon={<RefreshCw size={16} className={isRefreshingROI ? 'animate-spin' : ''} />}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowFullGraphModal(true)}
                    className="text-primary-500 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-200"
                  >
                    <Maximize2 size={18} />
                  </Button>
                  <span className={`text-xs px-2 py-1 rounded ${isLiveData
                    ? 'text-success-600 dark:text-success-400 bg-success-100 dark:bg-success-900/30'
                    : 'text-primary-500 dark:text-primary-400 bg-primary-100 dark:bg-primary-800'
                    }`}>
                    {isLiveData ? 'Live Data' : 'No Data'}
                  </span>
                </>
              )}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={400}>
            {user?.role === 'broker' ? (
              // Broker Commission Chart - Per Minute Data
              roiData.length > 0 ? (
                <AreaChart data={roiData}>
                  <defs>
                    <linearGradient id="commissionGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0A7E66" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#0A7E66" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="stroke-primary-200 dark:stroke-primary-700" />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: 'currentColor', fontSize: 11 }}
                    className="text-primary-600 dark:text-primary-400"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    tickFormatter={(value) => {
                      try {
                        const date = new Date(value);
                        if (isNaN(date.getTime())) return value;
                        // Show time for short periods, date for longer periods
                        if (['5m', '15m', '30m', '1h'].includes(roiPeriod)) {
                          return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                        } else if (roiPeriod === '24h') {
                          return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                        } else {
                          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        }
                      } catch (e) {
                        return value;
                      }
                    }}
                  />
                  <YAxis
                    tick={<CustomYAxisTick />}
                    className="text-primary-600 dark:text-primary-400"
                    domain={['auto', 'auto']}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '12px',
                    }}
                    content={<CustomTooltip />}
                    labelFormatter={(label) => {
                      try {
                        const date = new Date(label);
                        if (isNaN(date.getTime())) return label;
                        return date.toLocaleString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        });
                      } catch (e) {
                        return label;
                      }
                    }}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: '10px' }}
                    iconType="line"
                  />
                  <Area
                    type="monotone"
                    dataKey="commission"
                    stroke="#0A7E66"
                    fillOpacity={1}
                    fill="url(#commissionGradient)"
                    strokeWidth={2.5}
                    dot={roiData.length < 50 ? { r: 3, fill: '#0A7E66' } : false}
                    activeDot={{ r: 6, fill: '#0A7E66', stroke: '#fff', strokeWidth: 2 }}
                    name="Commission (AED)"
                  />
                </AreaChart>
              ) : (
                <div className="h-full flex items-center justify-center text-primary-600 dark:text-primary-400">
                  <div className="text-center">
                    <Activity size={56} className="mx-auto mb-4 opacity-30" />
                    <p className="font-medium text-lg">No commission data available</p>
                    <p className="text-sm mt-2">Click refresh to load latest data</p>
                  </div>
                </div>
              )
            ) : (
              // ROI Chart (for investors) - Per Minute Data
              roiData.length > 0 ? (
                <AreaChart data={roiData}>
                  <defs>
                    <linearGradient id="roiGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0A7E66" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#0A7E66" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="stroke-primary-200 dark:stroke-primary-700" />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: 'currentColor', fontSize: 11 }}
                    className="text-primary-600 dark:text-primary-400"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    tickFormatter={(value) => {
                      try {
                        const date = new Date(value);
                        if (isNaN(date.getTime())) return value;
                        // Show time for short periods, date for longer periods
                        if (['5m', '15m', '30m', '1h'].includes(roiPeriod)) {
                          return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                        } else if (roiPeriod === '24h') {
                          return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                        } else {
                          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        }
                      } catch (e) {
                        return value;
                      }
                    }}
                  />
                  <YAxis
                    tick={{ fill: 'currentColor', fontSize: 12 }}
                    className="text-primary-600 dark:text-primary-400"
                    tickFormatter={(value) => `${value.toFixed(2)}%`}
                    domain={['auto', 'auto']}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '12px',
                    }}
                    cursor={{ stroke: '#0A7E66', strokeWidth: 1, strokeDasharray: '4 4' }}
                    formatter={(value) => [`${parseFloat(value).toFixed(4)}%`, 'ROI Percentage']}
                    labelFormatter={(label) => {
                      try {
                        const date = new Date(label);
                        if (isNaN(date.getTime())) return label;
                        return date.toLocaleString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        });
                      } catch (e) {
                        return label;
                      }
                    }}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: '10px' }}
                    iconType="line"
                  />
                  <Area
                    type="monotone"
                    dataKey="roi"
                    stroke="#0A7E66"
                    fillOpacity={1}
                    fill="url(#roiGradient)"
                    strokeWidth={2.5}
                    dot={roiData.length < 50 ? { r: 3, fill: '#0A7E66' } : false}
                    activeDot={{ r: 6, fill: '#0A7E66', stroke: '#fff', strokeWidth: 2 }}
                    name="ROI %"
                  />
                  <Brush
                    dataKey="date"
                    height={30}
                    stroke="#0A7E66"
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return isNaN(date.getTime()) ? '' : date.toLocaleDateString();
                    }}
                  />
                </AreaChart>
              ) : (
                <div className="h-full flex items-center justify-center text-primary-600 dark:text-primary-400">
                  <div className="text-center">
                    <Activity size={56} className="mx-auto mb-4 opacity-30" />
                    <p className="font-medium text-lg">No ROI data available</p>
                    <p className="text-sm mt-2">Click refresh to load latest data</p>
                  </div>
                </div>
              )
            )}
          </ResponsiveContainer>
        </Card>
      )}

      {/* Payout Details Modal */}
      <Modal
        isOpen={showPayoutModal}
        onClose={() => {
          setShowPayoutModal(false);
          setSelectedPayout(null);
        }}
        title="Payout Details"
        size="md"
      >
        {selectedPayout && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-1">
                  Payout ID
                </p>
                <p className="text-sm font-semibold text-primary-900 dark:text-primary-50">
                  #{selectedPayout._id.toString().slice(-8)}
                </p>
              </div>
              <div className="p-4 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-1">
                  Date
                </p>
                <p className="text-sm font-semibold text-primary-900 dark:text-primary-50">
                  {new Date(selectedPayout.date).toISOString().split('T')[0]}
                </p>
              </div>
              <div className="p-4 bg-primary-50 dark:bg-primary-900/30 rounded-lg col-span-2">
                <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-1">
                  Amount
                </p>
                <p className="text-2xl font-bold text-primary-900 dark:text-primary-50 flex items-center gap-1">
                  <DirhamSymbol size={24} inline />
                  {(selectedPayout.amt || selectedPayout.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              {selectedPayout.admin_name && (
                <div className="p-4 bg-primary-50 dark:bg-primary-900/30 rounded-lg col-span-2">
                  <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-1">
                    Admin
                  </p>
                  <p className="text-sm font-semibold text-primary-900 dark:text-primary-50">
                    {selectedPayout.admin_name}
                  </p>
                </div>
              )}
              {selectedPayout.note && (
                <div className="p-4 bg-primary-50 dark:bg-primary-900/30 rounded-lg col-span-2">
                  <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-1">
                    Note
                  </p>
                  <p className="text-sm text-primary-900 dark:text-primary-50">
                    {selectedPayout.note}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Investments Modal */}
      <Modal
        isOpen={showInvestmentsModal}
        onClose={() => setShowInvestmentsModal(false)}
        title="My Investments"
        size="lg"
      >
        {portfolioData ? (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-1">
                  Total Invested
                </p>
                <p className="text-xl font-bold text-primary-900 dark:text-primary-50">
                  ${(kpis.totalInvestedAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="p-4 bg-success-50 dark:bg-success-900/30 rounded-lg">
                <p className="text-xs font-medium text-success-600 dark:text-success-400 uppercase tracking-wide mb-1">
                  Total Profit
                </p>
                <p className="text-xl font-bold text-success-900 dark:text-success-50 flex items-center gap-1">
                  <DirhamSymbol size={18} inline />
                  {(kpis.totalProfit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="p-4 bg-info-50 dark:bg-info-900/30 rounded-lg">
                <p className="text-xs font-medium text-info-600 dark:text-info-400 uppercase tracking-wide mb-1">
                  Total Transactions
                </p>
                <p className="text-xl font-bold text-info-900 dark:text-info-50">
                  {kpis.totalTransactions || 0}
                </p>
              </div>
            </div>

            {/* Investments Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-primary-200 dark:border-primary-700">
                    <th className="text-left px-4 py-3 font-semibold text-primary-900 dark:text-primary-50 text-sm">
                      Date
                    </th>
                    <th className="text-left px-4 py-3 font-semibold text-primary-900 dark:text-primary-50 text-sm">
                      Type
                    </th>
                    <th className="text-right px-4 py-3 font-semibold text-primary-900 dark:text-primary-50 text-sm">
                      Amount (USD)
                    </th>
                    <th className="text-right px-4 py-3 font-semibold text-primary-900 dark:text-primary-50 text-sm">
                      Profit (AED)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {portfolioData.transactions && portfolioData.transactions.length > 0 ? (
                    portfolioData.transactions.map((transaction, index) => (
                      <tr key={index} className="border-b border-primary-100 dark:border-primary-800 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors">
                        <td className="px-4 py-3 text-sm text-primary-900 dark:text-primary-50">
                          {new Date(transaction.date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${transaction.type === 'investment'
                            ? 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-400'
                            : 'bg-error-100 text-error-800 dark:bg-error-900/30 dark:text-error-400'
                            }`}>
                            {transaction.type === 'investment' ? (
                              <ArrowDown size={12} className="mr-1" />
                            ) : (
                              <ArrowUp size={12} className="mr-1" />
                            )}
                            {transaction.type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-right text-primary-900 dark:text-primary-50">
                          ${(transaction.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-right text-success-600 dark:text-success-400">
                          <span className="flex items-center justify-end gap-1">
                            <DirhamSymbol size={14} inline />
                            {(transaction.profit_earned || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-4 py-8 text-center text-primary-600 dark:text-primary-400">
                        No transactions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        )}
      </Modal>

      {/* ROI Calculator Modal */}
      <Modal
        isOpen={showROICalculator}
        onClose={handleCloseROICalculator}
        title="ROI Calculator"
        size="md"
      >
        <div className="space-y-6">
          <div className="space-y-4">
            {user?.role !== 'admin' && (
              <FormField
                label="Investment Amount (USD)"
                name="investment"
                type="number"
                value={roiCalculatorData.investment}
                onChange={handleROICalculatorChange}
                placeholder="Enter investment amount"
                sanitizeType="number"
                step="0.01"
                min="0"
                leftIcon={<DollarSign size={18} className="text-primary-400" />}
              />
            )}
            {user?.role === 'admin' && (
              <>
                <FormField
                  label="AED TO USDT RATE"
                  name="aedToUsdtRate"
                  type="number"
                  value={roiCalculatorData.aedToUsdtRate}
                  onChange={handleROICalculatorChange}
                  placeholder="Enter AED to USDT rate"
                  sanitizeType="number"
                  step="0.0001"
                  min="0"
                />
                <FormField
                  label="INR TO AED RATE"
                  name="inrToAedRate"
                  type="number"
                  value={roiCalculatorData.inrToAedRate}
                  onChange={handleROICalculatorChange}
                  placeholder="Enter INR to AED rate"
                  sanitizeType="number"
                  step="0.0001"
                  min="0"
                />
                <FormField
                  label="USDT SELLING INR"
                  name="usdtSellingInr"
                  type="number"
                  value={roiCalculatorData.usdtSellingInr}
                  onChange={handleROICalculatorChange}
                  placeholder="Enter USDT selling price in INR"
                  sanitizeType="number"
                  step="0.01"
                  min="0"
                />
                <FormField
                  label="TOTAL USDT SOLD"
                  name="totalUsdtSold"
                  type="number"
                  value={roiCalculatorData.totalUsdtSold}
                  onChange={handleROICalculatorChange}
                  placeholder="Enter total USDT sold"
                  sanitizeType="number"
                  step="0.01"
                  min="0"
                />
              </>
            )}
            {user?.role !== 'admin' && (
              <FormField
                label="ROI Percentage"
                name="basePercentage"
                type="number"
                value={roiCalculatorData.basePercentage}
                onChange={handleROICalculatorChange}
                placeholder="Enter ROI percentage"
                sanitizeType="number"
                step="0.01"
                min="0"
                max="100"
                helperText={
                  investorRoiData && investorRoiData.roi_min > 0 && investorRoiData.roi_max > 0
                    ? `Range: ${investorRoiData.roi_min}% - ${investorRoiData.roi_max}%`
                    : undefined
                }
              />
            )}
            <FormField
              label="Investment Period (Days)"
              name="days"
              type="number"
              value={roiCalculatorData.days}
              onChange={handleROICalculatorChange}
              placeholder="Enter number of days (0 for base calculation)"
              sanitizeType="number"
              step="1"
              min="0"
              leftIcon={<Calendar size={18} className="text-primary-400" />}
            />
          </div>

          <Button
            variant="primary"
            onClick={calculateROI}
            className="w-full"
            leftIcon={<Calculator size={18} />}
            disabled={
              user?.role === 'admin'
                ? (!roiCalculatorData.aedToUsdtRate || !roiCalculatorData.inrToAedRate || !roiCalculatorData.usdtSellingInr || !roiCalculatorData.totalUsdtSold)
                : (!roiCalculatorData.investment || !roiCalculatorData.days || !roiCalculatorData.basePercentage)
            }
          >
            Calculate ROI
          </Button>

          {calculatedROI && (
            <div className="mt-6 p-6 bg-primary-50 dark:bg-primary-900/50 rounded-xl border-2 border-primary-200 dark:border-primary-700">
              <h3 className="text-lg font-semibold text-primary-900 dark:text-primary-50 mb-4 flex items-center gap-2">
                <Percent className="text-primary-600 dark:text-primary-400" size={20} />
                ROI Calculation Results
              </h3>
              <div className="space-y-4">
                {user?.role === 'admin' ? (
                  <>
                    <div className="p-4 bg-success-50 dark:bg-success-900/20 rounded-lg border-2 border-success-200 dark:border-success-700">
                      <p className="text-xs font-medium text-success-600 dark:text-success-400 uppercase tracking-wide mb-2">
                        Return on Investment (ROI)
                      </p>
                      <p className="text-3xl font-bold text-success-600 dark:text-success-400">
                        {calculatedROI.percentage.toFixed(2)}%
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      <div className="p-3 sm:p-4 bg-white dark:bg-primary-800 rounded-lg border border-primary-200 dark:border-primary-700 min-w-0 overflow-hidden">
                        <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-1.5 truncate">
                          Profit AED
                        </p>
                        <p className="text-sm sm:text-base font-bold text-success-600 dark:text-success-400 flex items-center gap-1 min-w-0">
                          <DirhamSymbol size={16} inline className="text-success-600 dark:text-success-400 flex-shrink-0" />
                          <span className="min-w-0 break-words">
                            {calculatedROI.profitAed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </p>
                      </div>
                      <div className="p-3 sm:p-4 bg-white dark:bg-primary-800 rounded-lg border border-primary-200 dark:border-primary-700 min-w-0 overflow-hidden">
                        <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-1.5 truncate">
                          Cash in Hand AED
                        </p>
                        <p className="text-sm sm:text-base font-bold text-primary-900 dark:text-primary-50 flex items-center gap-1 min-w-0">
                          <DirhamSymbol size={16} inline className="text-primary-900 dark:text-primary-50 flex-shrink-0" />
                          <span className="min-w-0 break-words">
                            {calculatedROI.cashInHandAed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </p>
                      </div>
                      <div className="p-3 sm:p-4 bg-white dark:bg-primary-800 rounded-lg border border-primary-200 dark:border-primary-700 min-w-0 overflow-hidden">
                        <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-1.5 truncate">
                          AED TO USDT TOTAL
                        </p>
                        <p className="text-sm sm:text-base font-bold text-primary-900 dark:text-primary-50 flex items-center gap-1 min-w-0">
                          <DirhamSymbol size={16} inline className="text-primary-900 dark:text-primary-50 flex-shrink-0" />
                          <span className="min-w-0 break-words">
                            {calculatedROI.aedToUsdtTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </p>
                      </div>
                      <div className="p-3 sm:p-4 bg-white dark:bg-primary-800 rounded-lg border border-primary-200 dark:border-primary-700 min-w-0 overflow-hidden">
                        <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-1.5 truncate">
                          Total INR Sold
                        </p>
                        <p className="text-sm sm:text-base font-bold text-primary-900 dark:text-primary-50 break-words">
                          ₹{calculatedROI.totalInrSold.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                      {calculatedROI.days > 0 && (
                        <div className="p-3 sm:p-4 bg-white dark:bg-primary-800 rounded-lg border border-primary-200 dark:border-primary-700 min-w-0 overflow-hidden col-span-2">
                          <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-1.5 truncate">
                            Investment Period
                          </p>
                          <p className="text-sm sm:text-base font-bold text-primary-900 dark:text-primary-50 break-words">
                            {calculatedROI.days} {calculatedROI.days === 1 ? 'Day' : 'Days'} (TOTAL USDT SOLD multiplied by {calculatedROI.days} before calculation)
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      <div className="p-3 sm:p-4 bg-white dark:bg-primary-800 rounded-lg border border-primary-200 dark:border-primary-700 min-w-0 overflow-hidden">
                        <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-1.5 truncate">
                          Investment Amount
                        </p>
                        <p className="text-sm sm:text-base font-bold text-primary-900 dark:text-primary-50 flex items-center gap-1 min-w-0">
                          <span className="text-primary-900 dark:text-primary-50 flex-shrink-0">$</span>
                          <span className="min-w-0 break-words">
                            {calculatedROI.investment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </p>
                      </div>
                      <div className="p-3 sm:p-4 bg-white dark:bg-primary-800 rounded-lg border border-primary-200 dark:border-primary-700 min-w-0 overflow-hidden">
                        <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-1.5 truncate">
                          Total Return
                        </p>
                        <p className="text-sm sm:text-base font-bold text-success-600 dark:text-success-400 flex items-center gap-1 min-w-0">
                          <span className="text-success-600 dark:text-success-400 flex-shrink-0">$</span>
                          <span className="min-w-0 break-words">
                            {calculatedROI.returnAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      <div className="p-3 sm:p-4 bg-white dark:bg-primary-800 rounded-lg border border-primary-200 dark:border-primary-700 min-w-0 overflow-hidden">
                        <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-1.5 truncate">
                          Return Amount (AED)
                        </p>
                        <p className="text-sm sm:text-base font-bold text-success-600 dark:text-success-400 flex items-center gap-1 min-w-0">
                          <DirhamSymbol size={16} inline className="text-success-600 dark:text-success-400 flex-shrink-0" />
                          <span className="min-w-0 break-words">
                            {calculatedROI.returnAmountAED.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </p>
                      </div>
                      <div className="p-3 sm:p-4 bg-white dark:bg-primary-800 rounded-lg border border-primary-200 dark:border-primary-700 min-w-0 overflow-hidden">
                        <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-1.5 truncate">
                          Total Amount
                        </p>
                        <p className="text-sm sm:text-base font-bold text-primary-900 dark:text-primary-50 flex items-center gap-1 min-w-0">
                          <span className="text-primary-900 dark:text-primary-50 flex-shrink-0">$</span>
                          <span className="min-w-0 break-words">
                            {calculatedROI.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end pt-4 border-t border-primary-200 dark:border-primary-700">
            <Button
              variant="secondary"
              onClick={handleCloseROICalculator}
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add Transaction Modal */}
      <Modal
        isOpen={showTransactionModal}
        onClose={() => {
          setShowTransactionModal(false);
          setTransactionActiveTab('INR');
          setInrFormData({
            transactionAmountInr: '',
            date: new Date(),
            aedToUsdtRate: '',
            inrToAedRate: '',
            usdtSellingInr: ''
          });
          setUaeFormData({
            amount: '',
            date: new Date(),
            usdtBuyingRate: '',
            usdtSellingRate: ''
          });
        }}
        title={transactionActiveTab === 'INR' ? 'Add INR Transaction' : 'Add UAE Transaction'}
        size="lg"
      >
        <div className="space-y-4">
          {/* Tabs */}
          <div className="flex gap-2 border-b border-primary-200 dark:border-primary-700">
            <button
              type="button"
              onClick={() => setTransactionActiveTab('INR')}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${transactionActiveTab === 'INR'
                ? 'border-b-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400'
                : 'text-primary-500 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300'
                }`}
            >
              <div className="flex items-center justify-center gap-2">
                <ArrowLeftRight size={18} />
                INR
              </div>
            </button>
            <button
              type="button"
              onClick={() => setTransactionActiveTab('UAE')}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${transactionActiveTab === 'UAE'
                ? 'border-b-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400'
                : 'text-primary-500 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300'
                }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Flag size={18} />
                UAE
              </div>
            </button>
          </div>

          {/* INR Form */}
          {transactionActiveTab === 'INR' && (
            <form onSubmit={handleInrSubmit} className="space-y-4">
              <p className="text-sm text-primary-600 dark:text-primary-400 mb-4">Enter transaction details.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Transaction Amount (USDT)"
                  name="transactionAmountInr"
                  type="number"
                  step="0.01"
                  required
                  value={inrFormData.transactionAmountInr}
                  onChange={(e) => setInrFormData({ ...inrFormData, transactionAmountInr: e.target.value })}
                  placeholder="0.00"
                />
                <FormDatePicker
                  label="Transaction Date"
                  name="date"
                  required
                  value={inrFormData.date}
                  onChange={(e) => {
                    if (e.target.value) {
                      const dateMatch = e.target.value.match(/^(\d{4})-(\d{2})-(\d{2})/);
                      if (dateMatch) {
                        const year = parseInt(dateMatch[1], 10);
                        const month = parseInt(dateMatch[2], 10) - 1;
                        const day = parseInt(dateMatch[3], 10);
                        const dateValue = new Date(year, month, day);
                        setInrFormData({ ...inrFormData, date: dateValue });
                      }
                    }
                  }}
                  placeholderText="dd/mm/yyyy"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                    AED to USDT Rate
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.0001"
                      required
                      value={inrFormData.aedToUsdtRate}
                      onChange={(e) => setInrFormData({ ...inrFormData, aedToUsdtRate: e.target.value })}
                      placeholder="0.0000"
                      className="w-full px-4 py-2.5 pl-10 rounded-lg border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all"
                    />
                    <ArrowLeftRight size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400 dark:text-primary-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                    INR to AED Rate
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.0001"
                      required
                      value={inrFormData.inrToAedRate}
                      onChange={(e) => setInrFormData({ ...inrFormData, inrToAedRate: e.target.value })}
                      placeholder="0.0000"
                      className="w-full px-4 py-2.5 pl-10 rounded-lg border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all"
                    />
                    <ArrowLeftRight size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400 dark:text-primary-500" />
                  </div>
                </div>
              </div>
              <FormField
                label="USDT Selling INR"
                name="usdtSellingInr"
                type="number"
                step="0.0001"
                required
                value={inrFormData.usdtSellingInr}
                onChange={(e) => setInrFormData({ ...inrFormData, usdtSellingInr: e.target.value })}
                placeholder="0.0000"
              />
              <div className="flex gap-3 pt-4">
                <Button type="submit" variant="primary" className="flex-1" leftIcon={<ArrowLeftRight size={18} />}>
                  Add Transaction
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowTransactionModal(false);
                    setInrFormData({
                      transactionAmountInr: '',
                      date: new Date(),
                      aedToUsdtRate: '',
                      inrToAedRate: '',
                      usdtSellingInr: ''
                    });
                  }}
                  className="flex-1"
                  leftIcon={<X size={18} />}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}

          {/* UAE Form */}
          {transactionActiveTab === 'UAE' && (
            <form onSubmit={handleUaeSubmit} className="space-y-4">
              <p className="text-sm text-primary-600 dark:text-primary-400 mb-4">Enter transaction details.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Transaction Amount"
                  name="amount"
                  type="number"
                  step="0.01"
                  required
                  value={uaeFormData.amount}
                  onChange={(e) => setUaeFormData({ ...uaeFormData, amount: e.target.value })}
                  placeholder="0.00"
                />
                <FormDatePicker
                  label="Transaction Date"
                  name="date"
                  required
                  value={uaeFormData.date}
                  onChange={(e) => {
                    if (e.target.value) {
                      const dateMatch = e.target.value.match(/^(\d{4})-(\d{2})-(\d{2})/);
                      if (dateMatch) {
                        const year = parseInt(dateMatch[1], 10);
                        const month = parseInt(dateMatch[2], 10) - 1;
                        const day = parseInt(dateMatch[3], 10);
                        const dateValue = new Date(year, month, day);
                        setUaeFormData({ ...uaeFormData, date: dateValue });
                      }
                    }
                  }}
                  placeholderText="dd/mm/yyyy"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                    USDT Buying Rate
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.0001"
                      required
                      value={uaeFormData.usdtBuyingRate}
                      onChange={(e) => setUaeFormData({ ...uaeFormData, usdtBuyingRate: e.target.value })}
                      placeholder="0.0000"
                      className="w-full px-4 py-2.5 pl-10 rounded-lg border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all"
                    />
                    <ArrowDown size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400 dark:text-primary-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                    USDT Selling Rate
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.0001"
                      required
                      value={uaeFormData.usdtSellingRate}
                      onChange={(e) => setUaeFormData({ ...uaeFormData, usdtSellingRate: e.target.value })}
                      placeholder="0.0000"
                      className="w-full px-4 py-2.5 pl-10 rounded-lg border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all"
                    />
                    <ArrowUp size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400 dark:text-primary-500" />
                  </div>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" variant="primary" className="flex-1" leftIcon={<Flag size={18} />}>
                  Add Transaction
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowTransactionModal(false);
                    setUaeFormData({
                      amount: '',
                      date: new Date(),
                      usdtBuyingRate: '',
                      usdtSellingRate: ''
                    });
                  }}
                  className="flex-1"
                  leftIcon={<X size={18} />}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </div>
      </Modal>

      {/* Full Screen ROI Graph Modal */}
      <Modal
        isOpen={showFullGraphModal}
        onClose={() => setShowFullGraphModal(false)}
        title="ROI Live Movement (Detailed View)"
        size="full"
        className="max-w-[65vw]"
      >
        <div className="h-[80vh] w-full">
          {roiData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={roiData}>
                <defs>
                  <linearGradient id="roiGradientFull" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0A7E66" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#0A7E66" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="stroke-primary-200 dark:stroke-primary-700" />
                <XAxis
                  dataKey="date"
                  tick={{ fill: 'currentColor', fontSize: 11 }}
                  className="text-primary-600 dark:text-primary-400"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tickFormatter={(value) => {
                    try {
                      const date = new Date(value);
                      if (isNaN(date.getTime())) return value;
                      if (['5m', '15m', '30m', '1h'].includes(roiPeriod)) {
                        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                      } else if (roiPeriod === '24h') {
                        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                      } else {
                        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      }
                    } catch (e) {
                      return value;
                    }
                  }}
                />
                <YAxis
                  tick={{ fill: 'currentColor', fontSize: 12 }}
                  className="text-primary-600 dark:text-primary-400"
                  tickFormatter={(value) => `${value.toFixed(2)}%`}
                  domain={['auto', 'auto']}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '12px',
                  }}
                  cursor={{ stroke: '#0A7E66', strokeWidth: 1, strokeDasharray: '4 4' }}
                  formatter={(value) => [`${parseFloat(value).toFixed(4)}%`, 'ROI Percentage']}
                  labelFormatter={(label) => {
                    try {
                      const date = new Date(label);
                      if (isNaN(date.getTime())) return label;
                      return date.toLocaleString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      });
                    } catch (e) {
                      return label;
                    }
                  }}
                />
                <Legend iconType="line" />
                <Area
                  type="monotone"
                  dataKey="roi"
                  stroke="#0A7E66"
                  fillOpacity={1}
                  fill="url(#roiGradientFull)"
                  strokeWidth={2.5}
                  dot={roiData.length < 50 ? { r: 3, fill: '#0A7E66' } : false}
                  activeDot={{ r: 6, fill: '#0A7E66', stroke: '#fff', strokeWidth: 2 }}
                  name="ROI %"
                />
                <Brush
                  dataKey="date"
                  height={30}
                  stroke="#0A7E66"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return isNaN(date.getTime()) ? '' : date.toLocaleDateString();
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-primary-600 dark:text-primary-400">
              <div className="text-center">
                <Activity size={56} className="mx-auto mb-4 opacity-30" />
                <p className="font-medium text-lg">No ROI data available</p>
              </div>
            </div>
          )}
        </div>
      </Modal>
      {/* Extra Profit Allocation Modal */}
      <Modal
        isOpen={showAllocationModal}
        onClose={() => {
          setShowAllocationModal(false);
          setAllocationData(null);
          setAllocationFormData({
            recipientType: '',
            recipientId: '',
            amount: '',
            usePercentage: ''
          });
        }}
        title="Extra Profit Allocation"
        size="lg"
      >
        {allocationData && (
          <form onSubmit={handleAllocateProfit} className="space-y-6">
            <p className="text-sm text-primary-600 dark:text-primary-400">Distribute the extra profit.</p>

            {/* Info Cards */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="p-4 bg-primary-50 dark:bg-primary-900/50 border border-primary-200 dark:border-primary-700">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowLeftRight size={20} className="text-primary-600 dark:text-primary-400" />
                  <span className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase">Transaction</span>
                </div>
                <p className="text-lg font-bold text-primary-900 dark:text-primary-50">{allocationData.transactionType}</p>
              </Card>
              <Card className="p-4 bg-secondary-50 dark:bg-secondary-900/50 border border-secondary-200 dark:border-secondary-700">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle size={20} className="text-secondary-600 dark:text-secondary-400" />
                  <span className="text-xs font-medium text-secondary-600 dark:text-secondary-400 uppercase">Extra Amount</span>
                </div>
                <p className="text-lg font-bold text-secondary-900 dark:text-secondary-50 flex items-center gap-1">
                  <span className="text-sm">USDT</span>
                </p>
              </Card>
              <Card className="p-4 bg-success-50 dark:bg-success-900/50 border border-success-200 dark:border-success-700">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign size={20} className="text-success-600 dark:text-success-400" />
                  <span className="text-xs font-medium text-success-600 dark:text-success-400 uppercase">Extra Profit</span>
                </div>
                <p className="text-lg font-bold text-success-900 dark:text-success-50 flex items-center gap-1">
                  <DirhamSymbol size={16} inline />
                  {formatNumber(allocationData.profitAed)}
                  <span className="text-sm ml-1">AED</span>
                </p>
              </Card>
            </div>

            <Alert variant="warning">
              <div className="flex items-start gap-2">
                <div className="w-1 h-1 rounded-full bg-warning-600 dark:bg-warning-400 mt-1.5" />
                <div>
                  <p className="font-semibold text-warning-900 dark:text-warning-50">Allocation Required</p>
                  <p className="text-sm text-warning-700 dark:text-warning-300 mt-1">
                    The transaction amount exceeded the initial investment limit. Allocate extra profit to an investor/broker. The remaining amount will be automatically assigned to Admin.
                  </p>
                </div>
              </div>
            </Alert>

            <div>
              <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                <Users size={18} className="inline mr-2" />
                Select Investor or Broker
              </label>
              <FormReactSelect
                name="recipientId"
                required
                value={allocationFormData.recipientId}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const selectedUser = users.find(u => u._id === selectedId);
                  const selectedBroker = brokers.find(b => b._id === selectedId);
                  setAllocationFormData({
                    ...allocationFormData,
                    recipientId: selectedId,
                    recipientType: selectedUser ? 'investor' : selectedBroker ? 'broker' : ''
                  });
                }}
                options={[
                  ...users.filter(u => u.role === 'investor').map(u => ({
                    value: u._id,
                    label: `${u.firstName} ${u.lastName} (${u.email})`
                  })),
                  ...brokers.map(b => ({
                    value: b._id,
                    label: `${b.name} (${b.email})`
                  }))
                ]}
                placeholder="-- Select Investor or Broker --"
              />
              <p className="text-xs text-primary-500 dark:text-primary-400 mt-1">
                {users.filter(u => u.role === 'investor').length} Investors, {brokers.length} Brokers available
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                <DollarSign size={18} className="inline mr-2" />
                Profit Share Amount (AED)
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  value={allocationFormData.amount}
                  onChange={(e) => setAllocationFormData({ ...allocationFormData, amount: e.target.value, usePercentage: '' })}
                  placeholder="0"
                  className="w-full px-4 py-2.5 pr-16 rounded-lg border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-500 dark:text-primary-400">AED</span>
              </div>
              <p className="text-xs text-primary-500 dark:text-primary-400 mt-1">
                Maximum: {formatNumber(allocationData.profitAed)} AED
              </p>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant={allocationFormData.usePercentage === '50%' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => {
                    const amount = allocationData.profitAed * 0.5;
                    setAllocationFormData({
                      ...allocationFormData,
                      usePercentage: '50%',
                      amount: amount.toFixed(2)
                    });
                  }}
                >
                  50%
                </Button>
                <Button
                  type="button"
                  variant={allocationFormData.usePercentage === 'max' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setAllocationFormData({
                      ...allocationFormData,
                      usePercentage: 'max',
                      amount: allocationData.profitAed.toFixed(2)
                    });
                  }}
                >
                  Max
                </Button>
              </div>
            </div>

            {/* Allocation Summary */}
            <div className="border-t border-primary-200 dark:border-primary-700 pt-4">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle size={18} className="text-primary-600 dark:text-primary-400" />
                <h3 className="font-semibold text-primary-900 dark:text-primary-50">Allocation Summary</h3>
              </div>
              <div className="mb-4">
                <div className="flex justify-between text-xs text-primary-600 dark:text-primary-400 mb-1">
                  <span>Allocation Progress</span>
                  <span>
                    {allocationFormData.amount
                      ? `${((parseFloat(allocationFormData.amount) / allocationData.profitAed) * 100).toFixed(1)}%`
                      : '0.0%'}
                  </span>
                </div>
                <div className="w-full bg-primary-200 dark:bg-primary-700 rounded-full h-2">
                  <div
                    className="bg-primary-600 dark:bg-primary-400 h-2 rounded-full transition-all"
                    style={{
                      width: allocationFormData.amount
                        ? `${Math.min((parseFloat(allocationFormData.amount) / allocationData.profitAed) * 100, 100)}%`
                        : '0%'
                    }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-3 bg-success-50 dark:bg-success-900/50 border border-success-200 dark:border-success-700">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-2 h-2 rounded-full bg-success-600 dark:bg-success-400" />
                    <span className="text-xs font-medium text-success-600 dark:text-success-400">Allocated to User</span>
                  </div>
                  <p className="text-lg font-bold text-success-900 dark:text-success-50 flex items-center gap-1">
                    <DirhamSymbol size={14} inline />
                    {formatNumber(parseFloat(allocationFormData.amount) || 0)}
                  </p>
                  <p className="text-xs text-success-600 dark:text-success-400 mt-1">
                    {allocationFormData.amount
                      ? `${((parseFloat(allocationFormData.amount) / allocationData.profitAed) * 100).toFixed(1)}% of total`
                      : '0.0% of total'}
                  </p>
                </Card>
                <Card className="p-3 bg-primary-50 dark:bg-primary-900/50 border border-primary-200 dark:border-primary-700">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-2 h-2 rounded-full bg-primary-600 dark:bg-primary-400" />
                    <span className="text-xs font-medium text-primary-600 dark:text-primary-400">Remaining for Admin</span>
                  </div>
                  <p className="text-lg font-bold text-primary-900 dark:text-primary-50 flex items-center gap-1">
                    <DirhamSymbol size={14} inline />
                    {formatNumber(Math.max(0, allocationData.profitAed - (parseFloat(allocationFormData.amount) || 0)))}
                  </p>
                  <p className="text-xs text-primary-600 dark:text-primary-400 mt-1">
                    {allocationFormData.amount
                      ? `${(((allocationData.profitAed - parseFloat(allocationFormData.amount)) / allocationData.profitAed) * 100).toFixed(1)}% of total`
                      : '100.0% of total'}
                  </p>
                </Card>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAllocationModal(false);
                  setAllocationData(null);
                  setAllocationFormData({
                    recipientType: '',
                    recipientId: '',
                    amount: '',
                    usePercentage: ''
                  });
                }}
                className="flex-1"
                leftIcon={<X size={18} />}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                className="flex-1"
                leftIcon={<CheckCircle size={18} />}
              >
                Allocate Profit
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <BulkImportModal
        isOpen={showBulkImportModal}
        onClose={() => setShowBulkImportModal(false)}
        onSuccess={() => {
          setShowBulkImportModal(false);
          // Refresh data
          fetchKPIs();
          fetchChartData();
          fetchROIHistory(false, roiPeriod);
          if (user?.role === 'admin') {
            fetchAdminProfitHistory();
          }
        }}
      />

      {/* Daily Earnings Modal */}
      <Modal
        isOpen={showDailyEarningsModal}
        onClose={() => setShowDailyEarningsModal(false)}
        title="Daily Earnings History"
        size="lg"
      >
        {loadingEarnings ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-primary-500 dark:text-primary-400">
              <thead className="text-xs text-primary-700 uppercase bg-primary-50 dark:bg-primary-900/50 dark:text-primary-300">
                <tr>
                  <th scope="col" className="px-6 py-3">Date</th>
                  <th scope="col" className="px-6 py-3">Invested Amount</th>
                  <th scope="col" className="px-6 py-3">ROI %</th>
                  <th scope="col" className="px-6 py-3">Profit (AED)</th>
                </tr>
              </thead>
              <tbody>
                {dailyEarningsData.length > 0 ? (
                  dailyEarningsData.map((item, index) => (
                    <tr key={index} className="bg-white border-b dark:bg-primary-800 dark:border-primary-700 hover:bg-primary-50 dark:hover:bg-primary-700/50">
                      <td className="px-6 py-4 font-medium text-primary-900 dark:text-primary-50 whitespace-nowrap">
                        {new Date(item.date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-6 py-4">
                        ${(item.total_invested_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4">
                        {(item.roi_percentage || 0).toFixed(4)}%
                      </td>
                      <td className="px-6 py-4 text-success-600 dark:text-success-400 font-semibold">
                        <span className="flex items-center gap-1">
                          <DirhamSymbol size={12} inline />
                          {(item.daily_return || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center text-primary-400">
                      No earnings history found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Dashboard;
